import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { UpdateRecordService } from '../../core/services/update-record.service';
import { Update, UpdateElement } from '../../core/models/update.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ReactiveFormsModule } from '@angular/forms';
import { CreateUpdateRecordDto, PreviewPdfShiftClosureDto } from '../../core/dtos/update-record.dto';
import { UpdateService } from '../../core/services/update.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { ShiftClosureService } from '../../core/services/shift-closure.service';
import { Constants } from '../../shared/utils/constants';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UpdateRecord } from '../../core/models/update-record.model';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PdfPreviewModalComponent } from '../pdf-preview-modal/pdf-preview-modal.component';
import { AuthService } from '../../core/services/auth.service';

/*
 * MODO PREVISUALIZACIÓN:
 * Para activar el modo previsualización automática, pasar isPreviewMode: true en los datos del modal.
 * 
 * Ejemplo de uso:
 * 
 * // Para PREVISUALIZACIÓN (automática):
 * this.dialog.open(ModalComponent, {
 *   data: {
 *     type: 'cierre_turno',
 *     area: 'cierre_turno_diario',
 *     shiftClosureId: 123,
 *     isPreviewMode: true  // ← Esto activa el modo automático
 *   }
 * });
 * 
 * // Para CIERRE DE TURNO NORMAL (manual):
 * this.dialog.open(ModalComponent, {
 *   data: {
 *     type: 'cierre_turno',
 *     area: 'cierre_turno_diario',
 *     shiftClosureId: 123
 *     // Sin isPreviewMode o isPreviewMode: false → Flujo manual normal
 *   }
 * });
 */

/*
 * MODO MANUAL vs PREVISUALIZACIÓN:
 * 
 * - isPreviewMode: true  → Mantiene los steps originales (step: 1, step: 2, etc.) para navegación automática
 * - isPreviewMode: false → Agrupa todos los elementos en un solo step "Información del turno" para llenar manualmente
 * 
 * Flujo Manual: [Información del turno] → [Previsualización PDF] → [Finalización]
 * Flujo Preview: [Paso 1] → [Paso 2] → ... → [Previsualización PDF] → [Finalización] (automático)
 *
 */

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatStepperModule,
    MatButtonModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnInit {
  updateData!: Update;
  shiftReportData!: any;
  formGroup!: FormGroup;
  // Flags de visualización
  noElements: boolean = false; // true cuando no hay elementos que renderizar
  isShiftClosure: boolean = false; // true cuando el tipo corresponde a un cierre de turno
  groupedData: {
    steps: {
      step: number;
      grouped: { key: number; elements: UpdateElement[] }[];
      ungrouped: UpdateElement[];
    }[];
    noStep: { grouped: { key: number; elements: UpdateElement[] }[]; ungrouped: UpdateElement[] };
  } = {
    steps: [],
    noStep: { grouped: [], ungrouped: [] },
  };
  isReadOnly: boolean;
  isSaved: boolean = false;
  isPreviewMode: boolean = false;
  //@ViewChild(MatStepper) stepper!: MatStepper;
  stepper!: MatStepper;
  @ViewChild(MatStepper) set matStepper(component: MatStepper) {
  if (component) {
    this.stepper = component; // Asignamos la referencia a nuestra variable local
    
    // El momento preciso: Si no es jefe, mandamos al final
    if (this.data && !this.data.isBoss) {
      this.goToFinalStepDirectly();
    }
  }
}
  errorMessage: string | null = null;
  finalData: { chief: string; startDate: string; endDate: string; id: number } | null = null;
  finalShiftReportData: { chief: string; startDate: string; endDate: string; id: string } | null =
    null;
  pdfUrl: SafeResourceUrl | null = null;
  pdfPreviewLoaded = false;
  isLoadingPdf = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private updateRecordService: UpdateRecordService,
    private shiftClosureService: ShiftClosureService,
    private updateService: UpdateService,
    private dialogRef: MatDialogRef<ModalComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      type: string;
      area: string;
      readOnly?: boolean;
      record?: Update;
      saveOnlyUpdateRecord?: boolean;
      shiftClosureId?: number;
      isPreviewMode?: boolean; // Nueva propiedad para detectar modo previsualización
      skipPreview?: boolean; // Nueva propiedad para saltar paso de previsualización
      currentFormValues?: { [key: string]: any }; // Nueva propiedad para mantener valores actuales
      isHistorical?: boolean; // Nueva propiedad para marcar operaciones históricas
      closureMode?: string; // Nueva propiedad para especificar el modo de cierre
      closeUpdateRecord?: boolean; // Nueva propiedad para controlar si se cierran los registros al finalizar
      isBoss?:boolean
    },
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private authService: AuthService,
  ) {
    this.isReadOnly = data.readOnly ?? false;
    this.isPreviewMode = data.isPreviewMode ?? false;
  // Evaluar si el tipo es de cierre de turno para usarlo en el template
  this.isShiftClosure = this.isShiftClosureType(data.type);
  }

  ngOnInit(): void {
    this.isSaved = false;
  
    if (this.isShiftClosureType(this.data.type)) {
      console.log("Tipo 1")
      this.loadDataForShiftClosure();
    } else if (this.data.record) {
      this.loadRecordData(this.data.record);
      console.log("Tipo 2")
    } else {
      this.loadData(this.data.type, this.data.area);
      console.log("Tipo 3")
    }

    // if(!this.data.isBoss){

    //   setTimeout(()=> this.goToFinalStepDirectly(), 800)
    // }

    //if(!this.data.isBoss) this.goToFinalStepDirectly()
  }
  
  /**
   * Retorna true si el tipo recibido corresponde a un cierre de turno o informe de turno.
   */
  private isShiftClosureType(type: string): boolean {
    return type === Constants.TIPO_CIERE_TURNO_INFORME_DIARIO 
      || type === Constants.TIPO_CIERRE_TURNO
      || type === Constants.TIPO_CIERRE_TURNO_DCS
      || type === Constants.TIPO_CIERRE_TURNO_FERRICO
  || type === Constants.TIPO_CIERRE_TURNO_SALMUERA_II
  || type === Constants.TIPO_CIERRE_TURNO_SALMUERA_I
  || type === Constants.TIPO_CIERRE_TURNO_COMPRESION
  || type === Constants.TIPO_CIERRE_TURNO_ELECTROLIZADORES
  || type === Constants.TIPO_CIERRE_TURNO_SUBESTACION
  || type === Constants.TIPO_CIERRE_TURNO_MOVIMIENTO_PRODUCTO;
  }
  
  /**
   * Carga los datos para un cierre de turno.
   * Si existe shiftClosureId, se consulta por registros asociados;
   * de lo contrario se llama a loadData para cargar la estructura vacía.
   */
  private loadDataForShiftClosure(): void {
    if (this.data.shiftClosureId) {
      // Determinar el área según el rol del usuario
      const isDcs = this.authService.hasRole('Novedades_Operador_DCS') || 
                    this.authService.hasRole('DCS') || 
                    this.authService.hasRole('dcs');
      
      const isFerricoOrOthers = !!(
        this.authService.hasRole('Novedades_Operador_Ferrico') ||
        this.authService.hasRole('Novedades_Salmuera_I') ||
        this.authService.hasRole('Novedades_Salmuera_II') ||
        this.authService.hasRole('Novedades_Compresion') ||
        this.authService.hasRole('Novedades_Electrolizadores') ||
        this.authService.hasRole('Novedades_Subestacion') ||
        this.authService.hasRole('Novedades_Movimiento_Producto') ||
        this.authService.hasRole('FERRICO') ||
        this.authService.hasRole('ferrico')
      );

      const isJefeTurno = this.authService.hasRole('novedades');

      // DCS → cierre_turno_dcs
      // Jefe turno (novedades) → área original
      // Férrico y otros operadores → cierre_turno_ferrico
      // Default (rol no reconocido) → cierre_turno_ferrico
      let areaToUse: string;
      if (isDcs) {
        areaToUse = Constants.AREA_CIERRE_TURNO_DCS;
      } else if (isJefeTurno) {
        areaToUse = this.data.area;
      } else if (isFerricoOrOthers) {
        areaToUse = Constants.AREA_CIERRE_TURNO_FERRICO;
      } else {
        // Default para roles no reconocidos
        areaToUse = Constants.AREA_CIERRE_TURNO_FERRICO;
      }
      
      console.log(`loadDataForShiftClosure: isDcs=${isDcs}, isJefeTurno=${isJefeTurno}, isFerrico=${isFerricoOrOthers}, original area=${this.data.area}, using area=${areaToUse}`);
      
      this.updateRecordService.getUpdateRecordsByShiftClosureAndArea(this.data.shiftClosureId, [areaToUse])
        .subscribe({
          next: (records: UpdateRecord[]) => this.handleShiftClosureRecords(records),
          error: err => {
            console.error('Error al cargar el registro del cierre:', err);
            this.loadData(this.data.type, this.data.area);
          }
        });
    } else {
      // Si no hay shiftClosureId, carga los datos normalmente.
      this.loadData(this.data.type, this.data.area);
    }
  }
  
  /**
   * Procesa los registros obtenidos para un cierre de turno.
   * Si se encontró al menos un registro, se mapea y se carga el formulario;
   * en caso contrario, se carga la estructura vacía.
   */
  private handleShiftClosureRecords(records: UpdateRecord[]): void {
    console.log("Handling Shift Closure Records", records);
    if (records && records.length > 0) {
      //console.log("Records found", records);
      this.data.record = this.mapUpdateRecordToUpdate(records[0]);
      
      // Si tenemos valores actuales del formulario, aplicarlos al registro
      if (this.data.currentFormValues && this.isPreviewMode) {
        console.log('Applying current form values in preview mode:', this.data.currentFormValues);
        this.applyCurrentFormValues(this.data.record);
      }
      
      this.loadRecordData(this.data.record);
    } else {
      //console.log("No records found, loading empty structure");
      this.loadData(this.data.type, this.data.area);
    }
  }
  
  /**
   * Aplica los valores actuales del formulario al registro antes de cargarlo
   */
  private applyCurrentFormValues(record: Update): void {
    if (!this.data.currentFormValues || !record.updateElements) {
      return;
    }
    
    // Para cada elemento del registro, aplicar el valor actual si existe
    record.updateElements.forEach(element => {
      const elementId = element.id.toString();
      const currentValue = this.data.currentFormValues![elementId];
      
      // Si hay un valor actual y no es '0', aplicarlo
      if (currentValue !== undefined && currentValue !== null && currentValue !== '0' && currentValue !== '') {
        // Si no hay updateValues, crear el array
        if (!element.updateValues) {
          element.updateValues = [];
        }
        
        // Si ya existe un valor, actualizarlo
        if (element.updateValues.length > 0) {
          element.updateValues[0].value = currentValue;
        } 
        // Si no existe, crear uno nuevo
        else {
          element.updateValues.push({
            id: 0, // ID temporal, se asignará en el backend
            value: currentValue,
            createdUtc: new Date().toISOString()
          });
        }
      }
    });
  }
  

  private mapUpdateRecordToUpdate(record: UpdateRecord): Update {
    return {
      id: record.updateId,
      createdUtc: record.createdUtc,
      updateType: record.updateType,
      responsible: {
        id: record.responsible.id,
        username: record.responsible.username,
      },
      updateElements: record.updateElements.map(element => ({
        ...element,
        updateValues: element.updateValues,
      })),
      updateRecordId: record.id,
    };
  }

  loadRecordData(record: Update): void {
    if (!record || !record.updateElements) {
      console.error('El objeto record es inválido o incompleto:', record);
      return;
    }

    this.updateData = record;

    // Crear el FormGroup dinámico
    this.formGroup = this.fb.group(
      this.updateData.updateElements.reduce(
        (controls: Record<string, FormControl>, element) => {
          const rawValue = element.updateValues?.[0]?.value ?? '';

          let initialValue: boolean | string;
          if (element.element.name === 'CheckBox') {
            initialValue = rawValue === 'true';
          } else {
            initialValue = rawValue ?? '';
          }

          // Add validators based on element type
          const validators: any[] = [];
          
          // For CheckBox and Label elements, don't add required validator
          if (element.required && !this.isReadOnly && 
              element.element.name !== 'CheckBox' && 
              element.element.name !== 'Label') {
            validators.push(Validators.required);
          }
          
          // Add maxLength validator for TextArea fields
          if (element.element.name === 'TextArea') {
            validators.push(Validators.maxLength(200));
          }

          controls[element.id.toString()] = this.fb.control(
            { value: initialValue, disabled: this.isReadOnly },
            validators.length > 0 ? validators : null,
          );

          return controls;
        },
        {} as Record<string, FormControl>,
      ),
    );

  // Agrupar los elementos
  this.groupedData = this.groupElementsBasedOnMode(this.updateData.updateElements);
  // Evaluar si no hay elementos
  this.noElements = !this.updateData.updateElements || this.updateData.updateElements.length === 0;

    

    if (this.isReadOnly) {
      this.formGroup.disable();
    }
    
    /* Todo lo que viene a continuación solo debe ejecutarse cuando se está solicitando una "Previsualización".
       Caso contrario, debe omitirse. */
    if (this.isPreviewMode) {

      this.pdfPreviewLoaded = false;
      this.isLoadingPdf = true;
      
      // No es necesario rellenar automáticamente los campos con ceros, ahora respetamos los valores existentes
      // this.fillFormWithDefaultValues();
      this.savePartialUpdate();

      //Delay a la funcion de pasar al PDF:
      setTimeout(() => {
        this.goToPdfPreviewStep();
      }, 2000);
    } else {
      // Si NO está en modo previsualización y hay elementos, avanzar; si no hay, mostrar confirmación directa
      if (!this.noElements) {
        this.goToNextStep();
      }
    }

  }

  loadData(type: string, area: string): void {
    console.log("Load Data")
    this.updateService.getUpdateByTypeAndArea(type, area).subscribe({
      next: (data: Update[]) => {
        this.updateData = data[0];

        // Crear el FormGroup dinámico
        this.formGroup = this.fb.group(
          this.updateData.updateElements.reduce(
            (controls, element) => {
              const rawValue = element.updateValues?.[0]?.value ?? '';

              let initialValue: boolean | string;
              if (element.element.name === 'CheckBox') {
                initialValue = rawValue === 'true';
              } else {
                initialValue = rawValue ?? '';
              }

              // Add validators based on element type
              const validators: any[] = [];
              
              // For CheckBox and Label elements, don't add required validator
              if (element.required && !this.isReadOnly && 
                  element.element.name !== 'CheckBox' && 
                  element.element.name !== 'Label') {
                validators.push(Validators.required);
              }
              
              // Add maxLength validator for TextArea fields (200 characters for novedad)
              if (element.element.name === 'TextArea') {
                validators.push(Validators.maxLength(200));
              }

              controls[element.id.toString()] = this.fb.control(
                { value: initialValue, disabled: this.isReadOnly },
                validators.length > 0 ? validators : null,
              );

              return controls;
            },
            {} as Record<string, FormControl>,
          ),
        );

  // Agrupar los elementos
  this.groupedData = this.groupElementsBasedOnMode(this.updateData.updateElements);
  // Evaluar si no hay elementos
  this.noElements = !this.updateData.updateElements || this.updateData.updateElements.length === 0;

        if (this.isReadOnly) {
          // Deshabilitar todos los controles si es solo lectura
          this.formGroup.disable();
        }
        
        // Si estamos en modo previsualización, procesamos pero sin rellenar automáticamente los campos
        if (this.isPreviewMode) {
          // No es necesario rellenar con valores por defecto, respetamos los existentes
          // this.fillFormWithDefaultValues();
          
          // Delay para permitir que el formulario se inicialice completamente
          setTimeout(() => {
            this.savePartialUpdate();
            this.goToPdfPreviewStep();
          }, 700);
        } else {
          if (!this.noElements) {
            this.goToNextStep();
          }
        }
        
        this.cdr.detectChanges();
      },
      error: error => {
        console.error('Error al cargar los datos del update record:', error);
      },
    });
  }

  groupElements(elements: UpdateElement[]): {
    steps: {
      step: number;
      grouped: { key: number; elements: UpdateElement[] }[];
      ungrouped: UpdateElement[];
    }[];
    noStep: { grouped: { key: number; elements: UpdateElement[] }[]; ungrouped: UpdateElement[] };
  } {
    const stepsMap: Record<number, UpdateElement[]> = {};
    const noStepElements: UpdateElement[] = [];

    // Separar elementos con y sin step
    elements.forEach(element => {
      if (element.step != null) {
        if (!stepsMap[element.step]) {
          stepsMap[element.step] = [];
        }
        stepsMap[element.step].push(element);
      } else {
        noStepElements.push(element);
      }
    });

    // Agrupar elementos por step y ordenar los steps numéricamente
    const steps = Object.entries(stepsMap)
      .map(([step, stepElements]) => {
        const grouped = this.getGroupedElements(stepElements);
        return { step: +step, grouped: grouped.grouped, ungrouped: grouped.ungrouped };
      })
      .sort((a, b) => a.step - b.step); // Ordenar steps numéricamente

    // Agrupar elementos sin step
    const noStep = this.getGroupedElements(noStepElements);

    return { steps, noStep };
  }

  getGroupedElements(elements: UpdateElement[]): {
    grouped: { key: number; elements: UpdateElement[] }[];
    ungrouped: UpdateElement[];
  } {
    const groupedElements: { [key: number]: UpdateElement[] } = {};
    
    // Filtrar y ordenar elementos sin grupo por order
    const ungroupedElements = elements
      .filter(element => element.group === null) // Filtrar elementos sin grupo
      .sort((a, b) => a.order - b.order); // Ordenar elementos sin grupo por order

    // Agrupar elementos con grupo
    elements
      .filter(element => element.group !== null && element.group !== undefined) // Validar que tengan grupo
      .forEach(element => {
        if (!groupedElements[element.group!]) {
          groupedElements[element.group!] = [];
        }
        groupedElements[element.group!].push(element);
      });

    // Ordenar los elementos dentro de cada grupo por order
    Object.keys(groupedElements).forEach(key => {
      groupedElements[+key].sort((a, b) => a.order - b.order);
    });

    // Crear array de grupos ordenados
    let sortedGroupedElements = Object.keys(groupedElements).map(key => {
      return {
        key: +key,
        elements: groupedElements[+key] || [],
        // Guardar el orden mínimo del grupo para ordenar los grupos entre sí
        minOrder: Math.min(...groupedElements[+key].map(el => el.order))
      };
    });

    // Ordenar los grupos por el orden mínimo de sus elementos
    sortedGroupedElements = sortedGroupedElements.sort((a, b) => a.minOrder - b.minOrder);

    // Eliminar la propiedad minOrder que solo usamos para ordenar
    return {
      grouped: sortedGroupedElements.map(({ key, elements }) => ({ key, elements })),
      ungrouped: ungroupedElements,
    };
  }

  close(): void {
    // Cierra el modal
    this.dialogRef.close({ refreshed: true });
    // Forzar refresco de la página actual
    setTimeout(() => {
      window.location.reload();
    }, 50); // pequeño delay para permitir que el cierre del diálogo se procese
  }

  async submit(): Promise<void> {
    if (this.isReadOnly) return;

    // Debug: Log form status and values
    console.log('Form valid:', this.formGroup?.valid);
    console.log('Form values:', this.formGroup?.value);
    console.log('Form errors:', this.getFormErrors());

    // Validate form before submission
    if (!this.formGroup || !this.formGroup.valid) {
      const formErrors = this.getFormErrors();
      const errorMessages: string[] = [];
      
      Object.keys(formErrors).forEach(fieldId => {
        const element = this.updateData.updateElements.find(el => el.id.toString() === fieldId);
        const fieldLabel = element?.label || `Campo ${fieldId}`;
        
        // Skip Label and CheckBox fields from error messages
        if (element && (element.element.name === 'Label' || element.element.name === 'CheckBox')) {
          return;
        }
        
        if (formErrors[fieldId].required) {
          errorMessages.push(`${fieldLabel} es requerido`);
        }
        if (formErrors[fieldId].maxlength) {
          errorMessages.push(`${fieldLabel} excede el límite de caracteres`);
        }
      });
      
      this.errorMessage = errorMessages.length > 0 
        ? errorMessages.join('. ') + '.'
        : 'Por favor, complete todos los campos requeridos correctamente y verifique que no se exceda el límite de caracteres.';
      
      this.markFormGroupTouched(this.formGroup);
      
      // Debug: Show specific validation errors
      console.error('Validation errors:', formErrors);
      return;
    }

    const payload = this.buildPayload();

    // Determinar si es edición o creación
    if (this.data.record) {
      // Edición: Llamar al endpoint de actualización
      this.updateRecordService.updateUpdateRecord(this.data.record.id, payload).subscribe({
        next: response => {
          console.debug('Registro actualizado exitosamente:', response);
          this.dialogRef.close({ success: true });
        },
        error: error => {
          if (error.error && error.error.title === 'One or more validation errors occurred.') {
            this.errorMessage =
              'Se produjeron uno o más errores de validación. Valida que todos los campos tengan valor';
          } else if (error.error) {
            this.errorMessage = error.error;
          } else {
            this.errorMessage = 'Error al actualizar el registro.';
          }
          console.error('Error al actualizar el registro:', error);
        },
      });
    } else {
      // Creación: Llamar al endpoint de creación
      this.updateRecordService.createUpdateRecord(payload).subscribe({
        next: response => {
          console.debug('Registro creado exitosamente:', response);
          this.dialogRef.close({ success: true });
        },
        error: error => {
          if (error.error && error.error.title === 'One or more validation errors occurred.') {
            this.errorMessage =
              'Se produjeron uno o más errores de validación. Valida que todos los campos tengan valor';
          } else if (error.error) {
            this.errorMessage = error.error;
          } else {
            this.errorMessage = 'Error al crear el registro.';
          }
          console.error('Error al crear el registro:', error);
        },
      });
    }
  }

  private buildPayload(): CreateUpdateRecordDto {

    return {
      updateId: this.updateData.id,
      updateValues: this.updateData.updateElements
        .filter(element => element.element.name !== 'Label')
        .map(element => {
          const updateValueId = element.updateValues?.[0]?.id || null; // Validar si existe un valor
          let value = this.formGroup.value[element.id.toString()]; // Obtener valor del formulario

          // Si el valor es nulo o vacío, verificamos si hay valores actuales en caso de previsualización
          if (value === undefined || value === null || value === '') {
            // En modo previsualización, verificar si tenemos valores actuales pasados
            if (this.isPreviewMode && this.data.currentFormValues && 
                this.data.currentFormValues[element.id.toString()] !== undefined) {
              value = this.data.currentFormValues[element.id.toString()];
            } else {
              // Solo para campos realmente vacíos, usamos '0' en previsualización o '-' en otros casos
              value = this.isPreviewMode ? '0' : '-';
            }
          }

          // Garantizar que CheckBox siempre tenga una cadena como valor
          if (element.element.name === 'CheckBox') {
            value = value === true || value === 'true' ? 'true' : 'false';
          }
          return {
            updateValueId,
            updateElementId: element.id,
            value,
          };
        }),
      shiftClosureType: this.getShiftTypeDescription(),
      updateRecordId: this.updateData.updateRecordId,
    };
  }

  private buildPayloadForShiftClosure(): CreateUpdateRecordDto {
    return {
      updateId: this.updateData.id,
      updateValues: this.updateData.updateElements
        .filter(element => element.element.name !== 'Label')
        .map(element => {
          const rawId = element.updateValues?.[0]?.id;
          // Si no existe updateValues[0]?.id, se retorna undefined (no null)
          const updateValueId = rawId == null || rawId === 0 ? undefined : rawId;
          // Se obtiene el valor del control correspondiente en el formGroup
          let value = this.formGroup.value[element.id.toString()];
          
          // Si el valor es undefined o null
          if (value === undefined || value === null || value === '') {
            // Si estamos en modo de previsualización y ya tenemos datos del formulario pasados en data,
            // verificamos si hay un valor para este elemento
            if (this.isPreviewMode && this.data.currentFormValues && 
                this.data.currentFormValues[element.id.toString()] !== undefined) {
              value = this.data.currentFormValues[element.id.toString()];
            } 
            // Si no tenemos un valor personalizado, usamos '0' o '-'
            else {
              value = this.isPreviewMode ? '0' : '-';
            }
          }

          // Para CheckBox se normaliza a "true" o "false"
          if (element.element.name === 'CheckBox') {
            value = value === true || value === 'true' ? 'true' : 'false';
          }
          return {
            updateElementId: element.id,
            value,
            updateValueId,
          };
        }),
      shiftClosureType: this.getShiftTypeDescription(),
      updateRecordId: this.updateData.updateRecordId,
    };
  }

  getShiftTypeDescription(): string {
    // Mapear tipos conocidos a descriptores backend
    if (this.data?.type === Constants.TIPO_CIERRE_TURNO && this.data?.area === Constants.AREA_CIERRE_TURNO_DIARIO) {
      return Constants.TURNO_DIARIO;
    }
    if (this.data?.type === Constants.TIPO_CIERE_TURNO_INFORME_DIARIO) {
      return Constants.INFORME_TURNO;
    }
    // Para nuevos tipos (DCS/Férrico) usar informe_turno por defecto si el backend espera este descriptor
    if (this.data?.type === Constants.TIPO_CIERRE_TURNO_DCS || this.data?.type === Constants.TIPO_CIERRE_TURNO_FERRICO || this.data?.type === Constants.TIPO_CIERRE_TURNO_SALMUERA_II || this.data?.type === Constants.TIPO_CIERRE_TURNO_SALMUERA_I || this.data?.type === Constants.TIPO_CIERRE_TURNO_COMPRESION || this.data?.type === Constants.TIPO_CIERRE_TURNO_ELECTROLIZADORES || this.data?.type === Constants.TIPO_CIERRE_TURNO_SUBESTACION || this.data?.type === Constants.TIPO_CIERRE_TURNO_MOVIMIENTO_PRODUCTO) {
      return Constants.INFORME_TURNO;
    }
    return '';
  }

  ngAfterViewInit(): void {
    if (this.groupedData.steps.length > 0) {
      setTimeout(() => {
        if (!this.stepper) {
          console.error('MatStepper no está inicializado correctamente.');
        } else {
          console.debug('MatStepper inicializado correctamente.');
          this.cdr.detectChanges(); // Forzar detección de cambios si es necesario
        }
      }, 0);
    }
  }

  canGoBack(): boolean {
    return this.stepper ? this.stepper.selectedIndex > 0 : false;
  }

  isFirstStep(): boolean {
    return this.stepper ? this.stepper.selectedIndex === 0 : true;
  }

  isLastStep(): boolean {
    // Verifica si estás en el último paso generado dinámicamente (antes del paso manual)
    return this.stepper && this.stepper.selectedIndex === this.groupedData.steps.length - 1;
  }

  onStepChange(event: any): void {
    // Si venimos del paso de previsualización, reseteamos la bandera
    if (event.previouslySelectedIndex === this.groupedData.steps.length) {
      this.pdfPreviewLoaded = false;
    }
    
    // Si el paso seleccionado es el de previsualización 
    if (event.selectedIndex === this.groupedData.steps.length) {
      // Si skipPreview es true, avanzamos al paso de finalización automáticamente
      if (this.data.skipPreview === true) {
        setTimeout(() => {
          this.stepper.next(); // Avanzar al paso de finalización
        }, 0);
      } 

      /*
      // Si no debemos saltarnos la previsualización y aún no se ha cargado el PDF, lo generamos
      else if (!this.pdfPreviewLoaded) {
        console.log("PreviewLoaded");
        this.goToPdfPreviewStep();
      }
        */
    }
  }

  savePartialUpdate(): void {
    const payload = this.buildPayloadForShiftClosure();
    if (!this.data.shiftClosureId) {
      return;
    }
    // Si ya existe un registro, se actualiza; de lo contrario, se crea uno nuevo
    if (this.data.record && this.data.record.updateRecordId) {
      this.updateRecordService.updateUpdateRecord(this.data.record.updateRecordId, payload).subscribe({
        next: response => {
          console.debug('Registro actualizado exitosamente:', response);
          this.isSaved = true;
        },
        error: error => {
          this.handleSubmitError(error);
        },
      });
    } else {
      this.shiftClosureService
        .createAndAssociateUpdateRecord(payload, this.data.shiftClosureId)
        .subscribe({
          next: response => {
            console.debug('Registro creado exitosamente:', response);
            // Guardamos el registro en data para que futuras actualizaciones sean update
            if(this.data.record) {
              this.data.record.updateRecordId = response.id;
            }
            this.isSaved = true;
          },
          error: error => {
            this.handleSubmitError(error);
          },
        });
    }
  }

  goToNextStep(): void {
    this.savePartialUpdate();
    
    // Si estamos en el último paso dinámico (antes del paso de previsualización) y skipPreview es true,
    // saltamos directamente al paso de finalización
    if (this.isLastStep() && this.data.skipPreview === true) {
      // Saltar previsualización y avanzar a confirmación
      this.stepper.selectedIndex = this.groupedData.steps.length + 1;
    } 
    // En caso contrario, ir al siguiente paso normal
    else if (!this.isManualFinalStep()) {
      this.stepper.next();
    }
  }

  goToPreviousStep(): void {
    if (this.canGoBack()) {
      this.savePartialUpdate();
      this.stepper.previous();
    }
  }

  isFinalStep(): boolean {
    return this.stepper && this.stepper.selectedIndex === this.stepper.steps.length - 1;
  }

  isManualFinalStep(): boolean {
    // Verifica si estás en el último paso, que es el paso de "Finalización" añadido manualmente
    return this.stepper && this.stepper.selectedIndex === this.groupedData.steps.length + 1;
  }

  isPdfPreviewStep(): boolean {
    // El paso PDF está en el índice groupedData.steps.length
    return this.stepper && this.stepper.selectedIndex === this.groupedData.steps.length;
  }

  goToPdfPreviewStep(): void {
    this.isLoadingPdf = true;
    const payload = this.buildPayloadPreviewPdf();

    this.shiftClosureService.getShiftClosurePdfPreview(payload).subscribe({
      next: (pdfBlob: Blob) => {
        const pdfObjectUrl = URL.createObjectURL(pdfBlob);
        
        // Abrir en nueva pestaña
        const newTab = window.open(pdfObjectUrl, '_blank');
        
        if (newTab) {
          // Si se abrió en nueva pestaña, marcar como cargado
          this.pdfPreviewLoaded = true;
          this.isLoadingPdf = false;
          this.cdr.detectChanges();
          
          // Solo cerrar automáticamente si es modo previsualización
          if (this.isPreviewMode) {
            this.dialogRef.close();
          }
        } else {
          // Fallback: mostrar en el modal si no se puede abrir nueva pestaña
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfObjectUrl);
          this.pdfPreviewLoaded = true;
          this.isLoadingPdf = false;
          this.cdr.detectChanges();
          
          // Solo cerrar automáticamente si es modo previsualización
          if (this.isPreviewMode) {
            this.dialogRef.close();
          }
        }
      },
      error: err => {
        this.isLoadingPdf = false;
        console.error('Error al cargar el PDF:', err);
        
        // Si el error viene como un Blob, leerlo como texto
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorText = reader.result as string;
              this.errorMessage = errorText || 'Error al cargar el PDF de previsualización.';
              this.cdr.detectChanges();
            } catch (e) {
              this.errorMessage = 'Error al cargar el PDF de previsualización.';
              this.cdr.detectChanges();
            }
          };
          reader.onerror = () => {
            this.errorMessage = 'Error al cargar el PDF de previsualización.';
            this.cdr.detectChanges();
          };
          reader.readAsText(err.error);
        } else {
          // Extraer el mensaje de error del servidor en otros formatos
          let errorMsg = 'Error al cargar el PDF de previsualización.';
          if (err.error && typeof err.error === 'string') {
            errorMsg = err.error;
          } else if (err.error && err.error.message) {
            errorMsg = err.error.message;
          } else if (err.message) {
            errorMsg = err.message;
          }
          
          this.errorMessage = errorMsg;
          this.cdr.detectChanges();
        }
      },
    });

  }

  private handleSubmitError(error: any): void {
    if (error.error && error.error.title === 'One or more validation errors occurred.') {
      this.errorMessage =
        'Se produjeron uno o más errores de validación. Valida que todos los campos tengan valor';
    } else if (error.error) {
      this.errorMessage = error.error;
    } else {
      this.errorMessage = 'Error al guardar el registro.';
    }
    console.error('Error al enviar el registro:', error);
  }

  confirm(): void {
    this.errorMessage = '';
    const payload = this.buildPayload();

    // Si saveOnlyUpdateRecord es true, solo guardar el update record (para turnos históricos)
    if (this.data.saveOnlyUpdateRecord && this.data.shiftClosureId) {
      console.log('Guardando solo update record para turno histórico:', this.data.shiftClosureId);
      
      // Usar la lógica existente para crear/actualizar update record
      if (this.data.record?.updateRecordId) {
        // Actualizar record existente
        this.updateRecordService.updateUpdateRecord(this.data.record.updateRecordId, payload).subscribe({
          next: response => {
            console.log('Update record actualizado exitosamente');
            this.handleSaveOnlySuccess();
          },
          error: error => {
            this.handleSaveError(error);
          }
        });
      } else {
        // Crear nuevo record
        this.updateRecordService.createUpdateRecord(payload).subscribe({
          next: response => {
            console.log('Update record creado exitosamente');
            this.handleSaveOnlySuccess();
          },
          error: error => {
            this.handleSaveError(error);
          }
        });
      }
      return;
    }

    // Lógica para crear y cerrar turno completo
    // Si se especifica closeUpdateRecord en data, usar ese valor; si no, usar lógica por defecto
    const closeUpdateRecords =
      this.data?.closeUpdateRecord !== undefined
        ? this.data.closeUpdateRecord
        : (this.data?.type === Constants.TIPO_CIERRE_TURNO &&
           this.data?.area === Constants.AREA_CIERRE_TURNO_DIARIO);

  const overrideUserId = (closeUpdateRecords && this.data?.area === Constants.AREA_CIERRE_TURNO_DIARIO) ? 1 : undefined;
  this.shiftClosureService.createShiftClosureComplete(payload, closeUpdateRecords, this.data.shiftClosureId, overrideUserId, this.data.isHistorical, this.data.closureMode).subscribe({
      next: response => {
        console.log("Turno cerrado exitosamente, actualizando datos para mostrar finalización");
        this.finalData = {
          chief: response.chief,
          startDate: response.startDate,
          endDate: response.endDate,
          id: response.id,
        };
        console.log("Datos finales cargados:", this.finalData);

        // Si no hay stepper (por ejemplo, cierre sin elementos), mostrar finalización fuera del stepper
        if (!this.stepper || this.noElements || (this.groupedData?.steps?.length ?? 0) === 0) {
          this.finalShiftReportData = {
            chief: response.chief,
            startDate: response.startDate,
            endDate: response.endDate,
            id: String(response.id),
          };
        }

        // Si existe stepper, mover explícitamente al paso de “Finalización”
        if (this.stepper && (this.groupedData?.steps?.length ?? 0) >= 0) {
          // El índice del paso Finalización es la cantidad de steps dinámicos
          this.stepper.selectedIndex = this.groupedData.steps.length;
        }

        this.shiftClosureService.updateReportAvailability(response.id, true).subscribe({
          next: () => {
            console.log('Informe marcado como disponible.');
            // Forzar la actualización de la vista después de que se complete la operación
            this.cdr.detectChanges();
          },
          error: err => {
            console.error('Error al marcar el informe como disponible', err);
          }
        });

        // Establecer isSaved a true después de que los datos se hayan cargado completamente
        this.isSaved = true;
        console.log("isSaved ahora es:", this.isSaved);
        
        // Asegurarse de que la detección de cambios se ejecute para actualizar la vista
        setTimeout(() => {
          this.cdr.detectChanges();
          console.log("Vista actualizada mediante setTimeout, debería mostrar pantalla de finalización");
        }, 100);
      },
      error: error => {
        this.handleSaveError(error);
      },
    });
  }

  confirmShiftReport(): void {
    this.errorMessage = '';
    const payload = this.buildPayload();

    // Llamar al servicio createShiftClosure con el payload
  const overrideUserId = (this.data?.type === Constants.TIPO_CIERRE_TURNO && this.data?.area === Constants.AREA_CIERRE_TURNO_DIARIO) ? 1 : undefined;
  this.shiftClosureService.createShiftClosureComplete(payload, false, this.data.shiftClosureId, overrideUserId, this.data.isHistorical, this.data.closureMode).subscribe({
      next: response => {
        this.shiftReportData = null;
        this.finalShiftReportData = {
          chief: response.chief,
          startDate: response.startDate,
          endDate: response.endDate,
          id: response.id,
        };
      },
      error: error => {
        this.errorMessage = error.error;
      },
    });
  }

  downloadPdf(): void {
    if (!this.finalData?.id) {
      console.error('No se puede descargar el PDF porque no existe un ID de cierre final.');
      return;
    }
    const shiftClosureId = this.finalData.id;
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    const dateString = `${day}-${month}-${year}`;

    this.shiftClosureService.getShiftClosurePdf(shiftClosureId).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `shift_closure_${shiftClosureId}_${dateString}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: err => {
        console.error('Error al descargar el PDF:', err);
      },
    });
  }

  private buildPayloadPreviewPdf(): PreviewPdfShiftClosureDto {
    if(!this.data.shiftClosureId) {
      this.snackBar.open(`Error al obtener identificador del cierre`, 'Cerrar', { duration: 5000 });
    }
    return {
      updateId: this.updateData.id,
      updateValues: this.updateData.updateElements
        .filter(element => element.element.name !== 'Label')
        .map(element => {
          const updateValueId = element.updateValues?.[0]?.id || null; // Validar si existe un valor
          let value = this.formGroup.value[element.id.toString()]; // Obtener valor del formulario

          // Si el valor es nulo o vacío, asegurarnos de que sea '0' para pasar la validación
          if (value === undefined || value === null || value === '') {
            // Si tenemos valores personalizados, usarlos
            if (this.data.currentFormValues && 
                this.data.currentFormValues[element.id.toString()] !== undefined) {
              value = this.data.currentFormValues[element.id.toString()];
            } else {
              // Para previsualización de PDF, siempre usar '0' para valores vacíos
              value = '0';
            }
          }

          // Garantizar que CheckBox siempre tenga una cadena como valor
          if (element.element.name === 'CheckBox') {
            value = value === true || value === 'true' ? 'true' : 'false';
          }
          return {
            updateValueId,
            updateElementId: element.id,
            value,
          };
        }),
      shiftClosureType: this.getShiftTypeDescription(),
      updateRecordId: this.updateData.updateRecordId,
      shiftClosureId: this.data.shiftClosureId,
    };
  }

  openPdfPreview(): void {
    if (!this.data.shiftClosureId || !this.formGroup.valid) {
      this.snackBar.open('Por favor complete todos los campos requeridos antes de generar la previsualización.', 'Cerrar', { duration: 5000 });
      return;
    }

    // Construir el payload con los datos del formulario actual
    const previewPayload = this.buildPayloadPreviewPdf();

    // Abrir el modal de previsualización con los datos del formulario
    const dialogRef = this.dialog.open(PdfPreviewModalComponent, {
      data: { 
        shiftClosureId: this.data.shiftClosureId,
        previewPayload: previewPayload
      },
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '800px'
    });

    dialogRef.afterClosed().subscribe(() => {
      // No necesitamos hacer nada especial al cerrar la previsualización
    });
  }

  groupElementsBasedOnMode(elements: UpdateElement[]): {
    steps: {
      step: number;
      grouped: { key: number; elements: UpdateElement[] }[];
      ungrouped: UpdateElement[];
    }[];
    noStep: { grouped: { key: number; elements: UpdateElement[] }[]; ungrouped: UpdateElement[] };
  } {
    // Si está en modo previsualización, usar la agrupación normal por steps
    if (this.data.isPreviewMode === true) {
      return this.groupElements(elements);
    }
    
    // Si NO está en modo previsualización, agrupar todos los elementos con step en un solo paso
    return this.groupElementsInSingleStep(elements);
  }

  groupElementsInSingleStep(elements: UpdateElement[]): {
    steps: {
      step: number;
      grouped: { key: number; elements: UpdateElement[] }[];
      ungrouped: UpdateElement[];
    }[];
    noStep: { grouped: { key: number; elements: UpdateElement[] }[]; ungrouped: UpdateElement[] };
  } {
    const elementsWithStep: UpdateElement[] = [];
    const noStepElements: UpdateElement[] = [];

    // Separar elementos con y sin step
    elements.forEach(element => {
      if (element.step != null) {
        elementsWithStep.push(element);
      } else {
        noStepElements.push(element);
      }
    });

    // Ordenar elementos con step primero por step y luego por order
    elementsWithStep.sort((a, b) => {
      if (a.step !== b.step) {
        return a.step! - b.step!;
      }
      return a.order - b.order;
    });

    // Crear un solo paso que contenga todos los elementos con step
    const steps = [];
    if (elementsWithStep.length > 0) {
      const grouped = this.getGroupedElements(elementsWithStep);
      steps.push({ 
        step: 1, // Usar step 1 para todos los elementos
        grouped: grouped.grouped, 
        ungrouped: grouped.ungrouped 
      });
    }

    // Agrupar elementos sin step
    const noStep = this.getGroupedElements(noStepElements);

    return { steps, noStep };
  }

  getStepLabel(stepNumber: number): string {
    // Si no está en modo previsualización y hay un solo paso, mostrar etiqueta descriptiva
    if (this.data.isPreviewMode !== true && this.groupedData.steps.length === 1) {
      return 'Información del turno';
    }
    // Para modo previsualización o múltiples pasos, mostrar numeración normal
    return 'Paso ' + stepNumber;
  }

  /**
   * Rellena automáticamente los campos del formulario con valores predeterminados (ceros)
   * para la previsualización. Solo se usa en modo previsualización.
   */
  private fillFormWithDefaultValues(): void {
    if (!this.formGroup || !this.updateData || !this.updateData.updateElements) {
      console.error('No se puede rellenar el formulario: formulario o elementos no disponibles');
      return;
    }

    // Recorrer todos los elementos del formulario y establecer valores predeterminados
    this.updateData.updateElements.forEach(element => {
      if (element.element.name !== 'Label') {  // Excluir etiquetas
        const controlName = element.id.toString();
        let defaultValue: any = '0';  // Valor predeterminado para campos de texto

        // Asignar diferentes valores predeterminados según el tipo de campo
        switch (element.element.name) {
          case 'CheckBox':
            defaultValue = false;
            break;
          case 'ComboBox':
            // Para ComboBox usamos la primera opción disponible o cadena vacía
            defaultValue = element.options && element.options.length > 0 
              ? element.options[0].value 
              : '';
            break;
          case 'Date':
            // Para fechas usamos la fecha actual en formato YYYY-MM-DD
            const today = new Date();
            defaultValue = today.toISOString().split('T')[0];
            break;
          case 'Time':
            // Para horas usamos 00:00
            defaultValue = '00:00';
            break;
          case 'TextArea':
          case 'TextBox':
          default:
            defaultValue = '0';
            break;
        }

        // Establecer el valor en el control del formulario
        if (this.formGroup.controls[controlName]) {
          this.formGroup.controls[controlName].setValue(defaultValue);
        }
      }
    });

    console.log('Formulario rellenado automáticamente para previsualización');
  }

  // Method to get character count for a specific field
  getCharacterCount(fieldId: string): number {
    const control = this.formGroup?.get(fieldId);
    return control?.value?.length || 0;
  }

  // Method to check if field has exceeded max length
  isMaxLengthExceeded(fieldId: string): boolean {
    return this.getCharacterCount(fieldId) > 200;
  }

  // Helper method to mark all fields as touched to show validation errors
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  // Helper method to get form validation errors for debugging
  private getFormErrors(): any {
    const formErrors: any = {};
    
    if (!this.formGroup) return formErrors;
    
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      if (control && !control.valid && control.errors) {
        formErrors[key] = control.errors;
      }
    });
    
    return formErrors;
  }

  /**
   * Maneja el éxito al guardar solo update record (para turnos históricos)
   */
  private handleSaveOnlySuccess(): void {
    console.log('Update record guardado exitosamente para turno histórico');
    
    // Cerrar el modal con éxito e indicar que debe navegar a historical-shift
    this.dialogRef.close({ 
      success: true, 
      navigateToHistoricalShift: true 
    });
  }

  /**
   * Maneja errores de guardado
   */
  private handleSaveError(error: any): void {
    console.error('Error al guardar:', error);
    
    if (error.error && error.error.title === 'One or more validation errors occurred.') {
      this.errorMessage = 'Se produjeron uno o más errores de validación. Valida que todos los campos tengan valor';
    } else {
      this.errorMessage = 'Error al guardar los datos.';
    }
  }


  goToFinalStepDirectly(): void {
  if (!this.stepper) return;

  // Calculamos el índice del último paso (Finalización)
  // En tu lógica actual, suele ser la longitud de los pasos dinámicos + 1 (por el PDF)
  const finalStepIndex = this.stepper.steps.length - 1;

  // Forzamos el cambio de índice
  setTimeout(() => {
    this.stepper.selectedIndex = finalStepIndex;
    this.cdr.detectChanges();
  }, 0);
}
}
